import type { ReactElement } from 'react'
import { ContentConfirmDialogView } from './content-confirm-dialog.view'
import type { Translate } from './content-types'

/**
 * Props for {@link PublishDialogView}.
 *
 * @property immediate - True when the localization goes into force on publish.
 * @property effectiveStart - `YYYY-MM-DD` of the scheduled start (ignored when immediate).
 * @property replaces - The version this publication replaces, if any.
 */
export interface PublishDialogViewProps {
	t: Translate
	isOpen: boolean
	immediate: boolean
	effectiveStart: string
	replaces: number | null
	isBusy?: boolean
	error?: string | null
	onConfirm: () => void
	onCancel: () => void
}

/** Confirms a publication, stating when it goes into force and what it replaces (organism). */
export function PublishDialogView(props: PublishDialogViewProps): ReactElement {
	const { t } = props
	const when = props.immediate ? t('publish.immediate') : t('publish.scheduled', { date: props.effectiveStart })
	const lines = [when]
	if (props.replaces !== null) {
		lines.push(
			props.immediate
				? t('publish.replacesNow', { version: props.replaces })
				: t('publish.replacesFrom', { version: props.replaces, date: props.effectiveStart }),
		)
	}
	return (
		<ContentConfirmDialogView
			t={t}
			isOpen={props.isOpen}
			title={t('publish.title')}
			body={t('publish.body')}
			lines={lines}
			confirmLabel={t('lifecycle.publish')}
			isBusy={props.isBusy}
			error={props.error}
			onConfirm={props.onConfirm}
			onCancel={props.onCancel}
		/>
	)
}
